"use client";
import React, { useState } from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const PURPLE_LIGHT = '#a5b4fc';
const CARD = '#fbfdfd';

/* ── mockup illustrations, one per focus area ───────────────────────── */

function SearchIcon({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
            <circle cx="14" cy="14" r="8" stroke="currentColor" strokeWidth="2" />
            <path d="M20 20l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function GridIcon({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
            <rect x="4" y="4" width="10" height="10" rx="2.5" stroke="currentColor" strokeWidth="2" />
            <rect x="18" y="4" width="10" height="10" rx="2.5" stroke="currentColor" strokeWidth="2" />
            <rect x="4" y="18" width="10" height="10" rx="2.5" stroke="currentColor" strokeWidth="2" />
            <rect x="18" y="18" width="10" height="10" rx="2.5" stroke="currentColor" strokeWidth="2" />
        </svg>
    );
}
function BagIcon({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
            <path d="M6 10h20l-2 14H8L6 10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M11 10V8a5 5 0 0 1 10 0v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function PlayIcon({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
            <rect x="4" y="7" width="24" height="18" rx="4" stroke="currentColor" strokeWidth="2" />
            <path d="M13 12l8 4-8 4v-8z" fill="currentColor" />
        </svg>
    );
}
function SparkleIcon({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
            <path d="M16 4l3 8 8 3-8 3-3 8-3-8-8-3 8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
    );
}
function CheckCircleIcon({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
            <circle cx="15" cy="15" r="11.5" stroke="currentColor" strokeWidth="2" />
            <path d="M10 15l3.5 3.5L20 11.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function TargetIcon({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="2" />
            <circle cx="16" cy="16" r="5.5" stroke="currentColor" strokeWidth="2" />
            <circle cx="16" cy="16" r="1.5" fill="currentColor" />
        </svg>
    );
}
function TrendIcon({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
            <path d="M5 23L13 15L18 20L27 9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20 9h7v7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

/* 1 — two cascades of match-type pills, running parallel to each other */
function PillCascadeMockup({ active }: { active: boolean }) {
    const rows = [
        { left: { label: 'Broad match', tone: 'purple', ml: 0 }, right: { label: 'Negative match', tone: 'ink', mr: 0 } },
        { left: { label: 'Phrase match', tone: 'ink', ml: 28 }, right: { label: 'Dynamic match', tone: 'purple', mr: 28 } },
        { left: { label: 'Exact match', tone: 'purple', ml: 56 }, right: { label: 'Custom intent', tone: 'ink', mr: 56 } },
    ];
    return (
        <div className="flex flex-col gap-2.5">
            {rows.map((row, i) => (
                <div key={i} className="flex items-center justify-between">
                    <motion.div
                        animate={{ x: active ? 10 + i * 4 : 0 }}
                        transition={{ duration: 0.4, delay: active ? i * 0.06 : 0, ease: 'easeOut' }}
                        className="inline-flex items-center h-10 px-5 rounded-full text-sm font-semibold w-fit"
                        style={{
                            marginLeft: row.left.ml,
                            fontFamily: MANROPE,
                            background: row.left.tone === 'purple' ? PURPLE : INK,
                            color: '#fff',
                        }}
                    >
                        {row.left.label}
                    </motion.div>
                    <motion.div
                        animate={{ x: active ? -(10 + i * 4) : 0 }}
                        transition={{ duration: 0.4, delay: active ? i * 0.06 : 0, ease: 'easeOut' }}
                        className="inline-flex items-center h-10 px-5 rounded-full text-sm font-semibold w-fit"
                        style={{
                            marginRight: row.right.mr,
                            fontFamily: MANROPE,
                            background: row.right.tone === 'purple' ? PURPLE : INK,
                            color: '#fff',
                        }}
                    >
                        {row.right.label}
                    </motion.div>
                </div>
            ))}
        </div>
    );
}

/* 2 — cross-channel toggle */
function ToggleMockup({ active }: { active: boolean }) {
    return (
        <div className="flex flex-col gap-3">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ fontFamily: CLASH, color: 'rgba(8,4,0,0.4)' }}>
                Across 6 channels
            </span>
            <div
                className="relative flex items-center rounded-full h-14 w-full"
                style={{ background: active ? 'rgba(55,48,163,0.12)' : 'rgba(8,4,0,0.06)', transition: 'background-color 0.4s ease' }}
            >
                <motion.div
                    animate={{ left: active ? 'calc(100% - 52px)' : 4 }}
                    transition={{ duration: 0.45, ease: 'backOut' }}
                    className="absolute w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: INK, top: 4 }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="8" stroke={PURPLE_LIGHT} strokeWidth="1.6" />
                        <circle cx="12" cy="12" r="3" stroke={PURPLE_LIGHT} strokeWidth="1.6" />
                        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke={PURPLE_LIGHT} strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                </motion.div>
            </div>
        </div>
    );
}

/* 3 — feed performance bars */
function BarsMockup({ active }: { active: boolean }) {
    const bars = [0.55, 0.65, 0.6, 0.75, 0.7, 0.85, 0.8, 1];
    return (
        <div className="flex items-end justify-center gap-2 h-[110px]">
            {bars.map((h, i) => (
                <motion.div
                    key={i}
                    animate={{ height: `${h * (active ? 100 : 65)}%` }}
                    transition={{ duration: 0.4, delay: active ? i * 0.05 : 0, ease: 'easeOut' }}
                    className="w-6 rounded-full"
                    style={{ background: i === bars.length - 1 ? INK : i === 0 ? PURPLE : 'rgba(8,4,0,0.08)' }}
                />
            ))}
        </div>
    );
}

/* 4 — view-rate stat cluster */
function StatClusterMockup({ active }: { active: boolean }) {
    return (
        <div className="grid grid-cols-2 gap-2.5">
            <motion.div
                animate={{ scale: active ? 1.04 : 1 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="rounded-2xl p-3.5 flex flex-col gap-2"
                style={{ background: PURPLE }}
            >
                <span className="text-[9px] font-bold uppercase tracking-wider" style={{ fontFamily: CLASH, color: 'rgba(255,255,255,0.6)' }}>
                    View rate
                </span>
                <span className="text-2xl font-bold" style={{ fontFamily: CLASH, color: '#ffffff' }}>72%</span>
            </motion.div>
            <div className="rounded-2xl p-3.5 flex flex-col justify-between" style={{ background: INK }}>
                <span className="text-[9px] font-bold uppercase tracking-wider" style={{ fontFamily: CLASH, color: 'rgba(255,255,255,0.5)' }}>
                    Watch time
                </span>
                <svg width="100%" height="24" viewBox="0 0 70 24" fill="none" preserveAspectRatio="none">
                    <motion.path
                        d="M2 18L14 12L26 16L38 6L50 10L68 2"
                        stroke={PURPLE_LIGHT}
                        strokeWidth="2"
                        strokeLinecap="round"
                        fill="none"
                        animate={{ pathLength: active ? 1 : 0.35 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                    />
                </svg>
            </div>
        </div>
    );
}

/* 5 — scattered intent-signal badges, spread across the full card width */
function ScatterBadgesMockup({ active }: { active: boolean }) {
    const badges = [
        { Icon: SearchIcon, rotate: -6, top: 8, left: '2%' },
        { Icon: SparkleIcon, rotate: 5, top: 0, left: '19%' },
        { Icon: CheckCircleIcon, rotate: -4, top: 48, left: '11%' },
        { Icon: TargetIcon, rotate: 6, top: 14, left: '46%' },
        { Icon: TrendIcon, rotate: -5, top: 50, left: '63%' },
        { Icon: GridIcon, rotate: 4, top: 6, left: '82%' },
    ];
    return (
        <div className="relative h-[110px]">
            {badges.map(({ Icon, rotate, top, left }, i) => (
                <motion.div
                    key={i}
                    animate={{ opacity: active ? 1 : 0.55, scale: active ? 1 : 0.8, y: active ? 0 : 4, rotate }}
                    transition={{ duration: 0.35, delay: active ? i * 0.07 : 0, ease: 'backOut' }}
                    className="absolute w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ top, left, background: PURPLE, color: '#ffffff', boxShadow: '0 10px 24px -10px rgba(8,4,0,0.35)' }}
                >
                    <Icon size={18} />
                </motion.div>
            ))}
        </div>
    );
}

/* 6 — conversion notification bubble */
function ConversionBubbleMockup({ active }: { active: boolean }) {
    return (
        <motion.div
            animate={{ x: active ? 4 : 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="flex items-start gap-3 rounded-2xl p-4"
            style={{ background: 'rgba(8,4,0,0.04)', border: '1px solid rgba(8,4,0,0.08)' }}
        >
            <motion.span
                animate={{ scale: active ? [1, 1.15, 1] : 1 }}
                transition={{ duration: 0.5 }}
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                style={{ background: PURPLE, color: '#ffffff' }}
            >
                <CheckCircleIcon size={16} />
            </motion.span>
            <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold" style={{ fontFamily: MANROPE, color: INK }}>
                    New conversion — enquiry submitted
                </span>
                <span className="text-xs" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.4)' }}>
                    Just now
                </span>
            </div>
        </motion.div>
    );
}

const items = [
    {
        title: 'Google Search Ads',
        desc: 'Reach people actively searching for your products or services with campaigns structured around high-intent queries, relevant messaging and measurable conversions.',
        Mockup: PillCascadeMockup,
    },
    {
        title: 'Performance Max',
        desc: "Use Google's cross-channel inventory to reach potential customers across Search, YouTube, Display, Discover, Gmail and Maps where appropriate to your objectives.",
        Mockup: ToggleMockup,
    },
    {
        title: 'Google Shopping',
        desc: 'Put products in front of high-intent shoppers through structured product feeds, product segmentation and continuous campaign optimisation.',
        Mockup: BarsMockup,
    },
    {
        title: 'YouTube Advertising',
        desc: 'Use video to build awareness, consideration and demand while connecting campaigns to measurable audience and conversion objectives.',
        Mockup: StatClusterMockup,
    },
    {
        title: 'Keyword & Search Intent Strategy',
        desc: "We don't chase search volume for the sake of traffic. We identify the searches closest to your business objectives and allocate budget around intent and opportunity.",
        Mockup: ScatterBadgesMockup,
    },
    {
        title: 'Conversion Tracking',
        desc: 'We build campaigns around meaningful actions — enquiries, purchases, qualified leads and other business outcomes — rather than treating clicks as the end goal.',
        Mockup: ConversionBubbleMockup,
    },
];

function FocusCard({ item, index }: { item: typeof items[0]; index: number }) {
    const [hovered, setHovered] = useState(false);
    const Mockup = item.Mockup;
    return (
        <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (index % 2) * 0.1 + Math.floor(index / 2) * 0.08 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
            tabIndex={0}
            className="group flex flex-col gap-6 rounded-[28px] p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 outline-none focus-visible:ring-2"
            style={{ background: CARD, border: '1px solid rgba(8,4,0,0.08)', boxShadow: hovered ? '0 20px 40px -20px rgba(55,48,163,0.25)' : '0 2px 0 rgba(8,4,0,0.03)' }}
        >
            <div className="flex flex-col gap-2">
                <h3 className="text-xl font-bold leading-snug" style={{ fontFamily: CLASH, color: INK, letterSpacing: '0.01em' }}>
                    {item.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.55)' }}>
                    {item.desc}
                </p>
            </div>
            <div className="mt-auto pt-2">
                <Mockup active={hovered} />
            </div>
        </motion.div>
    );
}

export default function GoogleAdsWhatWeManage() {
    return (
        <section
            className="relative overflow-hidden pt-4 pb-20 md:pt-6 md:pb-28 px-6 md:px-16 lg:px-24"
            style={{ background: '#ffffff', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}
        >
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.035]"
                style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
            />

            <div className="max-w-[1200px] mx-auto flex flex-col gap-14 relative">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div className="flex flex-col gap-4">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-xs font-bold tracking-[0.2em] uppercase"
                            style={{ color: PURPLE, fontFamily: CLASH }}
                        >
                            What We Cover
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            What We Manage
                        </motion.h2>
                    </div>
                    <motion.span
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-sm font-mono"
                        style={{ color: 'rgba(8,4,0,0.35)', letterSpacing: '0.05em' }}
                    >
                        6 areas we cover
                    </motion.span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {items.map((it, i) => (
                        <FocusCard key={it.title} item={it} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
